import apiClient, { API_BASE_URL } from '@/lib/api-client';
import type { AdminBookingCreate, AdminBookingResponse, AvailableSlot } from '@/types/booking';
import { format, addDays } from 'date-fns';
import Cookies from 'js-cookie';

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  meta?: {
    timestamp: string;
    powered_by?: string;
  };
}

export interface BookingCreationResponse {
  booking: AdminBookingResponse;
  message: string;
}

class AdminBookingService {
  /**
   * Create a new booking as admin/staff
   * Allows booking past slots and choosing initial status
   */
  async createAdminBooking(data: AdminBookingCreate & { customer_id?: number | string }): Promise<AdminBookingResponse> {
    const response = await apiClient.post<ApiResponse<BookingCreationResponse>>(
      '/v1/admin/bookings',
      data
    );

    // Extract booking from nested response structure
    const booking = response.data.data.booking;

    // Transform snake_case to camelCase
    return {
      id: booking.id,
      availableSlotId: booking.availableSlotId,
      userId: booking.userId,
      customerEmail: booking.customerEmail,
      customerFirstName: booking.customerFirstName,
      customerLastName: booking.customerLastName,
      customerFullName: booking.customerFullName,
      customerPhone: booking.customerPhone,
      notes: booking.notes,
      status: booking.status,
      confirmedAt: booking.confirmedAt,
      cancelledAt: booking.cancelledAt,
      cancellationReason: booking.cancellationReason,
      organizationId: booking.organizationId,
      createdAt: booking.createdAt,
      updatedAt: booking.updatedAt,
      availableSlot: booking.availableSlot ? {
        id: booking.availableSlot.id,
        startTime: booking.availableSlot.startTime || booking.availableSlot.start_time,
        endTime: booking.availableSlot.endTime || booking.availableSlot.end_time,
        available: booking.availableSlot.available,
        provider: booking.availableSlot.provider,
        service: booking.availableSlot.service,
      } : undefined,
    };
  }

  /**
   * Get available slots for admin booking (properly scoped to current organization)
   */
  async getAvailableSlots(params?: {
    date?: string;
    providerId?: number;
    serviceId?: number;
  }): Promise<AvailableSlot[]> {
    const queryParams: Record<string, any> = {};

    if (params?.date) queryParams.date = params.date;
    if (params?.providerId) queryParams.provider_id = params.providerId;
    if (params?.serviceId) queryParams.service_id = params.serviceId;

    const response = await apiClient.get<ApiResponse<{ slots: any[] }>>(
      '/v1/admin/available_slots',
      { params: queryParams }
    );

    // Transform snake_case to camelCase
    return response.data.data.slots.map(slot => ({
      id: slot.id,
      providerId: slot.provider?.id || 0,
      serviceId: slot.service?.id || 0,
      startTime: slot.start_time,
      endTime: slot.end_time,
      available: slot.available,
      organizationId: slot.organization_id || 0,
      createdAt: slot.created_at || new Date().toISOString(),
      updatedAt: slot.updated_at || new Date().toISOString(),
      provider: slot.provider ? {
        id: slot.provider.id,
        userId: 0,
        displayName: slot.provider.name,
        bio: '',
        active: true,
        organizationId: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } : undefined,
      service: slot.service ? {
        id: slot.service.id,
        name: slot.service.name,
        description: '',
        duration: slot.service.duration_minutes,
        price: 0,
        active: true,
        organizationId: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } : undefined
    }));
  }

  /**
   * Get available slots using the new availability-based API with fallback to legacy
   * This generates slots dynamically from provider availability schedules
   */
  async getAvailabilitySlotsForDate(params: {
    serviceId: number;
    date: string;
    providerId?: number;
    organizationId?: number;
    excludeBookingId?: string | number;
  }): Promise<AvailableSlot[]> {
    const token = Cookies.get('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const queryParams = new URLSearchParams({
      service_id: params.serviceId.toString(),
      date: params.date,
    });

    if (params.providerId) {
      queryParams.append('provider_id', params.providerId.toString());
    }

    if (params.organizationId) {
      queryParams.append('organization_id', params.organizationId.toString());
    }

    if (params.excludeBookingId) {
      queryParams.append('exclude_booking_id', params.excludeBookingId.toString());
    }

    try {
      // Try the new availability-based API first
      const response = await fetch(`${API_BASE_URL}/v1/availability/slots?${queryParams}`, {
        headers,
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        // Transform to match AvailableSlot format
        return data.data.map((slot: any) => ({
          // Group-class slots come back with available_slot_id (numeric) so the
          // booking endpoint can route through the slot-based path. Non-group
          // slots have no DB row yet — synthesise an ID from provider+start.
          id: slot.available_slot_id ?? `${slot.provider_id}-${slot.start_time}`,
          startTime: slot.start_time,
          start_time: slot.start_time,
          endTime: slot.end_time,
          end_time: slot.end_time,
          available: true,
          provider: slot.provider_name ? {
            id: slot.provider_id,
            displayName: slot.provider_name,
            public_id: slot.provider_public_id,
          } : null,
          provider_id: slot.provider_id,
          service_id: slot.service_id,
          duration_minutes: slot.duration_minutes,
          max_capacity: slot.max_capacity,
          maxCapacity: slot.max_capacity,
          current_bookings: slot.current_bookings,
          currentBookings: slot.current_bookings,
          spots_remaining: slot.spots_remaining,
          spotsRemaining: slot.spots_remaining,
          is_full: slot.is_full,
          isFull: slot.is_full,
          waitlist_enabled: slot.waitlist_enabled,
        }));
      }
    } catch (error) {
      console.log('Availability API failed, trying legacy...', error);
    }

    // Fall back to legacy API
    return this.getAvailableSlots({
      serviceId: params.serviceId,
      date: params.date,
      providerId: params.providerId,
    });
  }

  /**
   * Get available dates in a date range using the new availability API with fallback
   */
  async getAvailableDatesInRange(params: {
    serviceId: number;
    startDate: string;
    endDate: string;
    providerId?: number;
    organizationId?: number;
    excludeBookingId?: string | number;
  }): Promise<Set<string>> {
    const token = Cookies.get('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const queryParams = new URLSearchParams({
      service_id: params.serviceId.toString(),
      start_date: params.startDate,
      end_date: params.endDate,
    });

    if (params.providerId) {
      queryParams.append('provider_id', params.providerId.toString());
    }

    if (params.organizationId) {
      queryParams.append('organization_id', params.organizationId.toString());
    }

    if (params.excludeBookingId) {
      queryParams.append('exclude_booking_id', params.excludeBookingId.toString());
    }

    try {
      // Try the new availability dates API first (more efficient)
      const response = await fetch(`${API_BASE_URL}/v1/availability/dates?${queryParams}`, {
        headers,
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        return new Set<string>(data.data.available_dates || []);
      }
    } catch (error) {
      console.log('Availability dates API failed, trying legacy...', error);
    }

    // Fall back to fetching each day individually using legacy API
    const datesSet = new Set<string>();
    const startDate = new Date(params.startDate);
    const endDate = new Date(params.endDate);
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    for (let i = 0; i < daysDiff; i++) {
      const date = addDays(startDate, i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const slots = await this.getAvailableSlots({
        serviceId: params.serviceId,
        date: dateStr,
        providerId: params.providerId,
      });
      if (slots.length > 0) {
        datesSet.add(dateStr);
      }
    }

    return datesSet;
  }

  /**
   * Create a booking - handles both availability-based and legacy slots
   */
  async createBookingFromAvailability(data: {
    serviceId: number;
    providerId: number;
    startTime: string;
    customerEmail: string;
    customerFirstName: string;
    customerLastName: string;
    customerPhone?: string;
    notes?: string;
    initialStatus: 'pending' | 'confirmed';
    customerId?: number | string;
  }): Promise<AdminBookingResponse> {
    const response = await apiClient.post<ApiResponse<BookingCreationResponse>>(
      '/v1/admin/bookings',
      {
        service_id: data.serviceId,
        provider_id: data.providerId,
        start_time: data.startTime,
        customer_email: data.customerEmail,
        customer_first_name: data.customerFirstName,
        customer_last_name: data.customerLastName,
        customer_phone: data.customerPhone,
        notes: data.notes,
        initial_status: data.initialStatus,
        customer_id: data.customerId,
      }
    );

    const booking = response.data.data.booking;

    return {
      id: booking.id,
      availableSlotId: booking.availableSlotId,
      userId: booking.userId,
      customerEmail: booking.customerEmail,
      customerFirstName: booking.customerFirstName,
      customerLastName: booking.customerLastName,
      customerFullName: booking.customerFullName,
      customerPhone: booking.customerPhone,
      notes: booking.notes,
      status: booking.status,
      confirmedAt: booking.confirmedAt,
      cancelledAt: booking.cancelledAt,
      cancellationReason: booking.cancellationReason,
      organizationId: booking.organizationId,
      createdAt: booking.createdAt,
      updatedAt: booking.updatedAt,
      availableSlot: booking.availableSlot ? {
        id: booking.availableSlot.id,
        startTime: booking.availableSlot.startTime || booking.availableSlot.start_time,
        endTime: booking.availableSlot.endTime || booking.availableSlot.end_time,
        available: booking.availableSlot.available,
        provider: booking.availableSlot.provider,
        service: booking.availableSlot.service,
      } : undefined,
    };
  }
  /**
   * Update booking details (customer info, notes)
   */
  async updateBooking(id: string | number, data: {
    customer_first_name?: string;
    customer_last_name?: string;
    customer_email?: string;
    customer_phone?: string;
    notes?: string;
  }): Promise<any> {
    const response = await apiClient.put<ApiResponse<any>>(
      `/v1/admin/bookings/${id}`,
      data
    );
    return response.data.data;
  }

  /**
   * Reschedule a booking to a new date/time
   */
  async rescheduleBooking(id: string | number, data: {
    start_time: string;
    provider_id?: number;
    service_id?: number;
  }): Promise<any> {
    const response = await apiClient.post<ApiResponse<any>>(
      `/v1/admin/bookings/${id}/reschedule`,
      data
    );
    return response.data.data;
  }

  /**
   * Preview a recurring (weekly) series before creating. Returns one entry per
   * planned occurrence with availability classification.
   */
  async previewRecurringBooking(data: {
    service_id: number;
    provider_id: number;
    first_start_time: string;
    recurrence: { occurrences?: number; end_date?: string; target_successful?: number };
    customer_email?: string;
  }): Promise<{
    occurrences: Array<{
      datetime: string;
      ok: boolean;
      reason: string | null;
      details?: Record<string, any>;
    }>;
    capped?: boolean;
    max_occurrences?: number;
  }> {
    const response = await apiClient.post<any>('/v1/admin/bookings/recurring/preview', data);
    // Server wraps payload in {success, data, meta}. Older planner responses
    // returned the payload at the top level — handle both shapes.
    return response.data.data ?? response.data;
  }

  /**
   * Create a recurring weekly series. Pass `force: true` to skip the unavailable
   * occurrences. Provide `force_create_availability_for` with datetimes to
   * create ad-hoc AvailabilityOverrides for slots flagged `no_availability`.
   */
  async createRecurringBooking(
    data: {
      service_id: number;
      provider_id: number;
      first_start_time: string;
      recurrence: { occurrences?: number; end_date?: string; target_successful?: number };
      customer_first_name: string;
      customer_last_name: string;
      customer_email: string;
      customer_phone?: string;
      customer_id?: number | string;
      notes?: string;
      initial_status: 'pending' | 'confirmed';
      force_create_availability_for?: string[];
    },
    options: { force?: boolean } = {},
  ): Promise<{
    series_id: string;
    count: number;
    adhoc_availability_created: number;
    bookings: Array<{ id: number; public_id: string; start_time: string; status: string }>;
  }> {
    const payload: Record<string, any> = { ...data };
    if (options.force) payload.force = true;
    const response = await apiClient.post<any>('/v1/admin/bookings/recurring', payload);
    return response.data.data ?? response.data;
  }

  /**
   * List the next existing group-class occurrences for a group service, so the
   * admin can pick which ones to enroll a customer into. Pass `customer_ids`
   * (the selected customers) to flag occurrences where every selected customer
   * is already booked in, so the UI can grey them out.
   */
  async getGroupOccurrences(params: {
    service_id: number;
    provider_id?: number;
    customer_ids?: Array<number | string>;
    weekdays?: number[];
    from_time?: string;
    to_time?: string;
  }): Promise<{
    occurrences: GroupOccurrence[];
    count: number;
    capped: boolean;
    filtered: boolean;
    max: number;
  }> {
    const response = await apiClient.get<any>(
      '/v1/admin/bookings/group_recurring/occurrences',
      { params },
    );
    return response.data.data ?? response.data;
  }

  /**
   * Enroll one customer into several existing group-class occurrences in one
   * call. Sends a single grouped confirmation email.
   */
  async createGroupRecurringBooking(data: {
    service_id: number;
    slot_ids: number[];
    customer_ids: Array<number | string>;
    notes?: string;
    initial_status: 'pending' | 'confirmed';
  }): Promise<{
    series_id: string;
    count: number;
    skipped: Array<{ slot_id: number; reason: string; customer_email?: string; needed?: number; available?: number }>;
    bookings: Array<{ id: number; public_id: string; slot_id: number; start_time: string; status: string }>;
  }> {
    const response = await apiClient.post<any>('/v1/admin/bookings/group_recurring', data);
    return response.data.data ?? response.data;
  }
}

export interface GroupOccurrence {
  slot_id: number;
  start_time: string;
  end_time: string;
  max_capacity: number;
  current_bookings: number;
  spots_remaining: number;
  full: boolean;
  waitlist_enabled: boolean;
  customer_already_booked: boolean;
  provider: { id: number; name: string } | null;
}

export const adminBookingService = new AdminBookingService();
